/**
 * L360 care-model event sender — the one pm-react sender for the L360 event
 * ingest (POST /workflows/care-models/events on the Scrips.Workflows engine).
 * Used by billing (I1, package sale) and the lab desk (L2/L5). It is the pm-react
 * twin of practitioner-react's `postL360Event`.
 *
 * Never-block rule: callers fire it with `void` after their own work has
 * succeeded; every error is caught here. One retry on a network error
 * (ApiError status 0) only, never on 4xx/5xx. No toast, no user-visible error.
 * Send only ids and flags — no name, no clinical detail.
 *
 * It imports `engineFetch` from `@/studio/services/workflow-api` for now; moving
 * `engineFetch` to `@/shared/api` is a follow-up.
 *
 * Dev-only: gated by VITE_L360_EVENTS === 'true' (dev-web.yml sets it; prod
 * does not), read at call time so tests can stub it.
 */

import { ApiError } from '@/shared/api/api-client';
import { engineFetch } from '@/studio/services/workflow-api';

export interface L360Event {
  eventType: string;
  patientId: string;
  sourceRef: string;
  idempotencyKey: string;
  occurredAt: string;
  payload: Record<string, unknown>;
}

/** Strict string compare, fail-closed: anything but "true" is off. */
export function isL360EventsEnabled(): boolean {
  return import.meta.env.VITE_L360_EVENTS === 'true';
}

/** Posts one event. Resolves always; never throws. */
export async function postL360Event(event: L360Event): Promise<void> {
  const send = () => engineFetch<void>('/workflows/care-models/events', { method: 'POST', body: event });
  try {
    try {
      await send();
    } catch (err) {
      if (!(err instanceof ApiError) || err.status !== 0) throw err;
      await send();
    }
  } catch (err) {
    // 409 (already received), 400 and the rest: nothing to do but log.
    console.warn('[l360-events] event not delivered', err);
  }
}
